import { __assign } from "tslib";
import BSSWatcher from './bss-watcher';
import BSSPackageValidation from '@main/externals/bss-package-validation/js/bss-package-validation';
import logger from '@main/externals/bss-package-logger/js/logger';
import { has } from 'lodash';
var _subscriberPrefix = 'component-';
/**
 * BSSAbstractComponent class
 */
var BSSAbstractComponent = /** @class */ (function () {
    /**
     * @desc BSSAbstractComponent constructor - generates all necessary objects and initialize abstract component
     * @param {Object} entityTemplate - simulator template
     * @param {String} containerSelector - component container selector
     * @param {Object} config - configuration of component
     * @param {EventManager} eventManager - instance of EventManager
     * @param {Formatter} formatter - instance of Formatter
     * @param {Resolution} Resolution - device Resolution
     * @param {Object} componentTemplates - component templates
     * @param {Object} toolbox - provides common libraries: jquery and lodash
     * @listens {bss-entity-revert-state-$ENTITY-INSTANCE-ID}
     */
    function BSSAbstractComponent(entityTemplate, containerSelector, config, eventManager, formatter, Resolution, componentTemplates, toolbox) {
        logger.info('AbstractComponent:  => constructor : componentId: ' +
            config.componentId +
            ' | Params: ', entityTemplate, containerSelector, config, eventManager, formatter, Resolution, componentTemplates, logger, toolbox);
        /**
         * @desc configuration Object
         * @type {object}
         * @private
         */
        this._config = config;
        /**
         * @desc this flag prevent update event subscription -> in _observeUpdateEvent
         * @type {boolean}
         */
        this.isUpdateSubscriptionOff = !!this._config.updateSubscriptionOff;
        /**
         * @desc this flag prevent request send on component change -> in _onOutputChange
         * @type {boolean}
         */
        this._isOnChangeOff = !!this._config.onChangeOff;
        /**
         * @desc this flag is for sending some additional event on component change (ex. for other components purposes)
         * @type {boolean}
         */
        this._sendAdditionalChangeEvent = !!this._config.sendAdditionalChangeEvent;
        /**
         * @desc component version, string is replace for component name and current version in gulp task correspond to data from package.json
         * @type {string}
         * @private
         */
        this._componentVersion = '@@name ver. @@version';
        /**
         * SubComponents object
         * {componentName: componentInstance}
         * @type {{}}
         * @public
         */
        this.subComponents = {};
        /**
         * SubComponents classes map,
         * extended by init classes that inherit abstract component
         * @type {{}}
         */
        this.subComponentsMap = {};
        /**
         * @desc entity template
         * @type {object}
         * @private
         */
        this._entityTemplate = entityTemplate;
        /**
         * @desc component container selector
         * @type {string}
         * @private
         */
        this._containerSelector = containerSelector;
        /**
         * @desc _EventManager
         * @type {Object}
         */
        this._eventManager = eventManager;
        /**
         * @desc Formatter
         * @type {Object}
         */
        this._formatter = formatter;
        /**
         * @desc device Resolution
         * @type {string}
         */
        this._resolution = Resolution;
        /**
         * @desc toolbox provides common libraries: $ -jquery, _ - lodash
         * @type {Object}
         * @private
         */
        this._toolbox = toolbox;
        /**
         * @type {jQuery|HTMLElement}
         * @private
         * @desc body, html content element
         */
        this._htmlBody = this._toolbox.$('html, body');
        /**
         * @type {jQuery|HTMLElement}
         * @private
         * @desc window object element
         */
        this._window = this._toolbox.$(window);
        /**
         * @desc Component error type: 'critical', 'minor', null
         * @type {String|null}
         * @private
         */
        this._errorType = null;
        /**
         * @desc prevent abstract data binding flag set in ui-desc
         * @type {Boolean}
         * @private
         */
        this._preventAbstractDataBinding =
            this._config.preventAbstractDataBinding || false;
        /**
         * @desc Component error object
         * @type {Object}
         * @property {String} entityInstanceId
         * @property {String} componentId
         * @property {String} componentSignificance - define componenet importance: 'critical', 'minor'
         * @property {String} errorSeverity - define componenet error importance: 'critical', 'minor'
         * @property {String} errorMsg - component error message information
         * @private
         */
        this._errorObj = {};
        /**
         * @desc css class added on this._formElementsWrapper when input in component is checked
         * @type {string}
         * @private
         */
        this._checkedClass = 'bss-checked';
        /**
         * @desc css class added on this._formElementsWrapper when input in component is disabled
         * @type {string}
         * @private
         */
        this._disabledClass = 'bss-disabled';
        /**
         * @desc css class added on select option
         * @type {string}
         * @private
         */
        this._optionClass = 'bss-select_option';
        this._updateLangFormatters();
        this.setConfig(config);
        this._prepareData();
        if (this.isNotRendered) {
            return;
        }
        /**
         * @desc component template
         * @type {jQuery|HTMLElement}
         * @private
         */
        this._componentTemplate = null;
        /**
         * @desc stores all inputs with data binding from template
         * @type {jQuery|HTMLElement}
         * @private
         */
        this._inputs = null;
        this._setComponentTemplate(componentTemplates);
        /**
         * @desc component scope
         * @type {jQuery|HTMLElement}
         * @private
         */
        this._componentScope = this._componentTemplate;
        /**
         * @desc wrapper for form elements
         * @type {jQuery|HTMLElement|*}
         * @private
         */
        this._formElementsWrapper = this._componentTemplate;
        /**
         * @desc Main form element selector used to initialize form elements validation
         * @type {string}
         */
        this.formSelector = "#".concat(this._config.componentId, "-form");
        /**
         * @desc conatines event name that should be trrigered after recalculation
         * @type {Object}
         * @private
         */
        this._tagEventOnThrow = null;
        /**
         * @desc its array of created BSSWatcher objecs
         * @type {Array<BSSWacher>}
         * @private
         */
        this._watchers = [];
        this._prepareComponentScope();
        this._prepareFormsData();
        /**
         * @desc true when there is critical error during components update - do not update components
         * or when it shouldn't be updated by purpose
         * @type {Boolean}
         * @private
         */
        this._stopUpdateFlag = this._config.stopUpdateFlag;
        /**
         * @desc true when there is critical error during components update - revert state event
         * @type {Boolean}
         * @private
         */
        this._isRevertStateEvent = false;
        this._eventManager.subscribe('bss-entity-revert-state-' + this._config.entityInstanceId, function () {
            this._revertStateCallback();
        }.bind(this), this.subscriberPrefix + this._config.componentId);
        /**
         * @desc id of the last update
         * @type {String}
         * @private
         */
        this._lastUpdateTimestamp = '';
        this.setTemplate();
        /**
         * @desc reference to the component in the DOM
         * @type {String}
         * @private
         */
        this._componentEl = document.querySelector("#".concat(this._config.componentId));
        /**
         * @desc component element in the entity scope
         * @type {jQuery|HTMLElement}
         * @private
         */
        this._$componentEl = this._entityTemplate.find(this._componentEl);
        this._setWatchers();
        this._addVisibilityBreakpointsFunctionality();
        this._observeUpdateEvent(this.update.bind(this));
        if (!this._preventAbstractDataBinding) {
            this._bindData(this._config);
        }
        this._initializeTagging();
    }
    /**
     * @desc Create simple  AJAX request with additional possibilities.
     * @param success {function}
     * @param error {function}
     * @param type {string}
     * @param dataType {string}
     * @param contentType {string}
     * @param url {string}
     * @param data {{}}
     * @public
     */
    BSSAbstractComponent.prototype.createSimpleRequest = function (_a) {
        var success = _a.success, error = _a.error, data = _a.data, _b = _a.url, url = _b === void 0 ? '' : _b, _c = _a.type, type = _c === void 0 ? '' : _c, _d = _a.dataType, dataType = _d === void 0 ? '' : _d, _e = _a.contentType, contentType = _e === void 0 ? '' : _e;
        logger.info('AbstractComponent' +
            this._config.componentId +
            ' => function : createSimpleRequest');
        var _f = this._config.requestConfiguration || {}, configUrl = _f.url, configType = _f.type, configDataType = _f.dataType, configContentType = _f.contentType;
        var requestUrl = url || configUrl;
        if (requestUrl) {
            this._toolbox.$.ajax({
                context: this,
                url: requestUrl,
                type: type || configType || 'POST',
                dataType: dataType || configDataType,
                contentType: contentType || configContentType,
                data: data,
                success: success,
                error: error,
            });
        }
        else {
            logger.error('AbstractComponent => function: createSimpleRequest: url is required to make request');
        }
    };
    /**
       * @desc Replaces langFormatter by formatter per current language for all records
       * @example
       * Input (UI-description):
       *  "langFormatter": {
        "paths": [
            "variable",
            "sliderHandle.sliderHandleFormatter"
        ],
        "values": {
          "fr": "befrDuration",
          "nl": "benlDuration"
        }
      },
       "variable":"<value data-formatter='langFormatter' data-value='{product.id}.duration'></value>",
       "sliderHandle":{
        "sliderHandleFormatter":"langFormatter"
      },
       ...
       * The output config for fr language will look like:
       * "variable":"<value data-formatter='befrDuration' data-value='{product.id}.duration'></value>",
       "sliderHandleFormatter":"befrDuration"
       * @private
       */
    BSSAbstractComponent.prototype._updateLangFormatters = function () {
        var _this = this;
        logger.info('AbstractComponent' +
            this._config.componentId +
            ' => function : _updateLangFormatters');
        //update formatter per language
        if (this._config.langFormatter) {
            var productId = this.getProductId();
            var language_1 = this._config.resultData[productId].language;
            this._config.langFormatter.paths.forEach(function (path) {
                _this._replaceLangFormatter(path, language_1);
            });
        }
    };
    /**
     * Load custom validation methods, eg rule-pattern
     * Method should be called from child component when needed
     * @public
     */
    BSSAbstractComponent.prototype.loadCustomValidationMethods = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function: _loadCustomValidationMethods');
        /**
         * @desc indicates whether form validation is needed
         * @type {Boolean}
         * @private
         */
        this._validationSettings = this._toolbox._.merge({}, {
            componentId: "".concat(this._config.componentId, "_container_validator"),
            validation: {
                additionalHideErrors: '',
                ajaxValidation: false,
                defaultErrors: true,
                formSelector: this.formSelector,
                throwEventOnError: '',
                throwEventOnSuccess: '',
            },
        }, this._config.validationSettings);
        if (this._validationSettings) {
            this._validation = new BSSPackageValidation(this._validationSettings, this._toolbox, this._eventManager, this._entityTemplate);
            if (this._initializeValidationCustomMethods) {
                this._eventManager.subscribe('bss-entity-created-' + this._config.entityInstanceId, this._initializeValidationCustomMethods.bind(this), this.subscriberPrefix + this._config.componentId);
            }
            this._validation.loadCustomMethods();
        }
    };
    /**
     * Return current product id,
     * @returns {string}
     */
    BSSAbstractComponent.prototype.getProductId = function () {
        logger.info('AbstractComponent' +
            this._config.componentId +
            ' => function : getProductId');
        var componentProductId = this._config.productId[0];
        return componentProductId === 'general'
            ? this._config.productId[1]
            : componentProductId;
    };
    /**
       * @desc Replaces langFormatter by formatter per current language for single record
       * @example
       * Input (UI-description):
       *  "langFormatter": {
        "paths": [
            "sliderHandle.sliderHandleFormatter"
        ],
        "values": {
          "fr": "befrDuration",
          "nl": "benlDuration"
        }
      },
       "sliderHandle":{
        "sliderHandleFormatter":"langFormatter"
      },
       ...
       * The output config for fr language will look like:
       * "sliderHandleFormatter":"befrDuration"
       * @param {string} path - path to string including formatter to be replaced, eg "sliderHandle.sliderHandleFormatter"
       * @param {string} language - language from resultData, eg 'fr', 'nl'
       * @private
       */
    BSSAbstractComponent.prototype._replaceLangFormatter = function (path, language) {
        logger.info('AbstractComponent' +
            this._config.componentId +
            ' => function : _replaceLangFormatter');
        var currentLangFormatter = this._config.langFormatter.values[language];
        var pathNodes = path.split('.');
        // Get last node value (remove from the general list) to support cases with nested path, eg sliderConfig.sliderFormatter
        var lastNode = pathNodes.pop();
        var inputValue = this._config;
        // Go to the one before last node
        // last node will be developed after forEach ineration
        pathNodes.forEach(function (pathNode) {
            if (pathNode) {
                inputValue = inputValue[pathNode];
            }
        });
        // Update only last value:
        // String can not be kept as a reference, so it should be gotten separatly.
        if (inputValue) {
            inputValue[lastNode] = inputValue[lastNode].replace('langFormatter', currentLangFormatter);
        }
    };
    /**
     * @desc Change visibility of component depending on resolution list set in the compoentns definition
     * By default component is visible on all resolution breakpoints.
     * Component should have componentId applied on its wrapper, eg <div componentId={config.compontntId}></div>
     * !important Values on the visibilityBreakponts list should be lowercased
     * Empty visibilityBreakpoints array will hide the component on the each breakpoint, eg visibilityBreakpoints: []
     * Unset visibilityBreakpoints will show the compoennt on all breakpoints: eg visibilityBreakpoints: null
     * @private
     */
    BSSAbstractComponent.prototype._addVisibilityBreakpointsFunctionality = function () {
        logger.info("AbstractComponent".concat(this._config.componentId, " => function : _addVisibilityBreakpointsFunctionality"));
        var visibilityBreakpoints = this._config.visibilityBreakpoints;
        if (this._componentEl && visibilityBreakpoints) {
            this._updateVisibilityBreakpoints();
            this._addVisibilityBreakpointsEvent();
        }
    };
    /**
     * @desc Change visibility of component depending on resolution list set in the compoents definition.
     * By default component is visible on all resolution breakpoints.
     * For comparizon are used breakpoints from resolutionsConfig - definitiona of Resolution instance:
     * http://stash.makolab.net/projects/BSSEX/repos/bss-package-resolutions/browse/src/js/resolutions.js
     * @private
     */
    BSSAbstractComponent.prototype._updateVisibilityBreakpoints = function () {
        logger.info("AbstractComponent".concat(this._config.componentId, " => function : _updateVisibilityBreakpoints"));
        var isVisible = this._config.visibilityBreakpoints.includes(this._resolution.getCurrent());
        if (isVisible) {
            this._componentEl.classList.remove('bss-resolution-hide');
        }
        else {
            this._componentEl.classList.add('bss-resolution-hide');
        }
    };
    /**
     * @desc Add event listener on resolution change to change visibility of component
     * depending on resolution list set in the compoentns definition for current resolution.
     * By default component is visible on all resolution breakpoints.
     * @private
     */
    BSSAbstractComponent.prototype._addVisibilityBreakpointsEvent = function () {
        logger.info("AbstractComponent".concat(this._config.componentId, " => function : _addVisibilityBreakpointsEvent"));
        this._eventManager.subscribe('bss-device-type-changed', this._updateVisibilityBreakpoints.bind(this), this.subscriberPrefix + this._config.componentId);
    };
    /**
     * @desc fire method checkState
     * @private
     */
    BSSAbstractComponent.prototype._triggerWatchers = function () {
        for (var i = 0, len = this._watchers.length; i < len; i++) {
            this._watchers[i].checkState('', this._config);
        }
    };
    /**
     * @desc set flags when entity reverts its state
     * @private
     */
    BSSAbstractComponent.prototype._revertStateCallback = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _revertStateCallback');
        this._stopUpdateFlag = true;
        this._isRevertStateEvent = true;
    };
    /**
     * @desc set components watchers
     * @private
     */
    BSSAbstractComponent.prototype._setWatchers = function () {
        if (!this._config.watcher) {
            logger.info('AbstractComponent: ' +
                this._config.componentId +
                ' => function : _setWatchers' +
                ' | NO WATCHERS');
            return;
        }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _initializeTagging');
        for (var i = 0, len = this._config.watcher.length; i < len; i++) {
            var watcher = new BSSWatcher(this._componentTemplate, this._config, this._config.watcher[i], this._toolbox, this._eventManager);
            this._watchers.push(watcher);
        }
    };
    /**
     * @desc initialize tagging
     * Tagging possible initialize on DOM event like 'click', 'change', ... selector element which will be listens.
     * Tagging possible initialize with EventManager just set onEvent = 'publisher' and selector fill by subscribed.
     * @param {jQuery} element
     * @private
     */
    BSSAbstractComponent.prototype._initializeTagging = function (element) {
        if (element === void 0) { element = null; }
        if (!this._config.tagEvent && !element) {
            logger.info('AbstractComponent: ' +
                this._config.componentId +
                ' => function : _initializeTagging' +
                ' | NO TAGGING');
            return;
        }
        else if (element) {
            logger.info('AbstractComponent: ' +
                this._config.componentId +
                ' => function : _initializeTagging' +
                ' | Static link tagging');
            var tagObject = {
                onEvent: 'click',
                throwEvent: 'staticLink_' + this._config.productId[0] + '_click',
                throwAfterCalculation: false,
            };
            element.on(tagObject.onEvent + '.' + this._config.componentId + '_staticLink', this._onTaggingEvent.bind(this, tagObject.throwEvent, tagObject.throwAfterCalculation));
            return;
        }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _initializeTagging');
        for (var i = 0, len = this._config.tagEvent.length; i < len; i++) {
            var tagObject = this._config.tagEvent[i];
            if (tagObject.onEvent === 'publisher') {
                this._eventManager.subscribe(tagObject.selector, this._onTaggingEvent.bind(this, tagObject.throwEvent, tagObject.throwAfterCalculation), this.subscriberPrefix + this._config.componentId);
            }
            else {
                if (tagObject.parent) {
                    this._componentTemplate.on(tagObject.onEvent + '.' + this._config.componentId, this._onTaggingEvent.bind(this, tagObject.throwEvent, tagObject.throwAfterCalculation));
                }
                else {
                    this._componentTemplate
                        .find(tagObject.selector)
                        .on(tagObject.onEvent + '.' + this._config.componentId, this._onTaggingEvent.bind(this, tagObject.throwEvent, tagObject.throwAfterCalculation));
                }
            }
        }
    };
    /**
     * @desc callback on tagging event
     * @param {String} throwEvent
     * @param {Boolean} throwAfterCalculation
     * @param {Event} [ev]
     * @protected
     */
    BSSAbstractComponent.prototype._onTaggingEvent = function (throwEvent, throwAfterCalculation, ev) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _onTaggingEvent | Params', throwEvent, throwAfterCalculation, ev);
        var target = ev ? this._toolbox.$(ev.currentTarget) : null;
        if (!throwAfterCalculation) {
            this._throwTaggingEvent(throwEvent, throwAfterCalculation, target);
        }
        else {
            this._tagEventOnThrow = throwEvent;
            this._tagTarget = target;
        }
    };
    /**
     * @desc throw tagging event - update resultData
     * @param {String} throwEvent
     * @param {Boolean} [recalculation=false] - true when event should be thrown after recalculation
     * @emits {throwEvent} emits event every time
     * @private
     */
    BSSAbstractComponent.prototype._throwTaggingEvent = function (throwEvent, recalculation) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (recalculation === void 0) { recalculation = false; }
        var resultData = this._config.resultData;
        var resultDataFlat = (this._toolbox || {}).resultDataFlat;
        var productId = this._config.productId[0];
        var objThrowEventTag = {
            throwEvent: throwEvent,
            resultData: null,
            productTexts: null,
            productId: productId,
            generalTexts: ((_b = (_a = this._config) === null || _a === void 0 ? void 0 : _a.texts) === null || _b === void 0 ? void 0 : _b.general) || ((_d = (_c = this._toolbox) === null || _c === void 0 ? void 0 : _c.layoutTexts) === null || _d === void 0 ? void 0 : _d.general),
            recalculation: recalculation,
        };
        if (has(resultData, productId)) {
            objThrowEventTag.resultData = resultData[productId];
            if (objThrowEventTag.resultData) {
                objThrowEventTag.resultData.integrationMode =
                    ((_e = resultDataFlat[productId]) === null || _e === void 0 ? void 0 : _e.integrationMode) || null;
            }
        }
        if (!objThrowEventTag.resultData && has(resultDataFlat, productId)) {
            objThrowEventTag.resultData = resultDataFlat[productId];
        }
        if (has(this._config.texts, productId)) {
            objThrowEventTag.productTexts = this._config.texts[productId];
        }
        // add flow params to toolbox resultData to be used in tagging (UK OA)
        if (has(resultDataFlat, [productId, 'flowParams'])) {
            objThrowEventTag.resultData.flowParams =
                resultDataFlat[productId].flowParams;
        }
        (_g = (_f = this._toolbox.dxpTagging) === null || _f === void 0 ? void 0 : _f[this._toolbox.currentEntity.entityId]) === null || _g === void 0 ? void 0 : _g.publish(throwEvent, __assign(__assign({}, objThrowEventTag.resultData), { triggerName: this._triggerName || this._config.componentType }), this._config.taggingTriggerName || '');
    };
    /**
     * @desc prepares all form components for data binding
     * @private
     */
    BSSAbstractComponent.prototype._prepareFormsData = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _prepareFormsData');
        this._prepareFormElements();
    };
    /**
     * @desc prepares all form components for data binding
     * @param {Object} componentTemplates
     * @private
     */
    BSSAbstractComponent.prototype._setComponentTemplate = function (componentTemplates) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _setComponentTemplate' +
            ' | Params', componentTemplates);
        if (typeof this._config.componentTemplate !== 'undefined' &&
            typeof componentTemplates[this._config.componentTemplate] !== 'undefined') {
            this._componentTemplate = this._toolbox.$(componentTemplates[this._config.componentTemplate](this._config));
        }
        else {
            this._componentTemplate = this._toolbox.$(componentTemplates.defaultTemplate(this._config));
        }
    };
    /**
     * @desc prepares inputs form component for data binding
     * @private
     */
    BSSAbstractComponent.prototype._prepareFormElements = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _prepareFormElements');
        var elements = this._componentTemplate.find('input, select');
        for (var i = 0, length_1 = elements.length; i < length_1; i++) {
            var $element = this._toolbox.$(elements[i]);
            var elementConfig = $element.data('config');
            var $elementValue = this._toolbox.$(elementConfig);
            this._setFormElementDataAttr(elementConfig, $element);
            $element.attr('data-formatter', $elementValue.attr('data-formatter'));
        }
        this._outputObject = this._getValueObject(this._config.configuration);
        this._inputs = elements.filter('input[data-value], input[data-text], input[data-checked], input[data-disabled]');
        this._inputs.on('change.' + this._config.componentId, this._onInputChange.bind(this));
        this._select = elements.filter('select[data-value], select[data-text], select[data-checked], select[data-disabled], select[data-dropname]');
        this._select.on('change.' + this._config.componentId, this._onSelectChange.bind(this));
    };
    /**
     * @desc function adds data attributes from tag value on input
     * @param {String} value - tag value string
     * @param {jQuery|HTMLElement} $input - input
     * @private
     */
    BSSAbstractComponent.prototype._setFormElementDataAttr = function (value, $input) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _setFormElementDataAttr' +
            ' | Params', value, $input);
        var $value = this._toolbox.$(value);
        var inputData = $value.data();
        this._toolbox._.forIn(inputData, function (v, key) {
            $input.attr('data-' + key, $value.attr('data-' + key));
        });
    };
    /**
     * @desc callback on change on form elements, currently: inputs (type: text, radio, checkbox)
     * @param {Event} ev
     * @throws {Wrong data bind.} when tag is bound to not existing data
     * @private
     */
    BSSAbstractComponent.prototype._onInputChange = function (ev) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _onInputChange' +
            ' | Params', ev);
        var $elm = this._toolbox.$(ev.currentTarget);
        var resultDataObj = {};
        try {
            switch ($elm.attr('type')) {
                case 'checkbox':
                    if ($elm.is(':checked')) {
                        if (typeof this._config.resultData[this._config.productId[0]][this._outputObject.checked] !== 'undefined' ||
                            typeof this._config.resultData[this._config.productId[0]][this._outputObject.value] !== 'undefined') {
                            resultDataObj[this._outputObject.checked] = 'true';
                        }
                        else {
                            resultDataObj.components = {};
                            resultDataObj.components.checkboxComponents = [];
                            resultDataObj.components.checkboxComponents.push({
                                name: $elm[0].name,
                                checked: 'true',
                            });
                        }
                        this._formElementsWrapper.addClass(this._checkedClass);
                    }
                    else {
                        if (typeof this._config.resultData[this._config.productId[0]][this._outputObject.checked] !== 'undefined' ||
                            typeof this._config.resultData[this._config.productId[0]][this._outputObject.value] !== 'undefined') {
                            resultDataObj[this._outputObject.checked] = 'false';
                        }
                        else {
                            resultDataObj.components = {};
                            resultDataObj.components.checkboxComponents = [];
                            resultDataObj.components.checkboxComponents.push({
                                name: $elm[0].name,
                                checked: 'false',
                            });
                        }
                        this._formElementsWrapper.removeClass(this._checkedClass);
                    }
                    break;
                case 'radio':
                    this._formElementsWrapper.addClass(this._checkedClass);
                    if (typeof this._config.resultData[this._config.productId[0]][this._outputObject.checked] !== 'undefined' ||
                        typeof this._config.resultData[this._config.productId[0]][this._outputObject.value] !== 'undefined') {
                        resultDataObj[this._outputObject.checked] = $elm.val();
                    }
                    else {
                        resultDataObj.components = {};
                        resultDataObj.components.radioButtonComponents = [];
                        resultDataObj.components.radioButtonComponents.push({
                            name: $elm[0].name,
                            checked: $elm.val(),
                        });
                    }
                    break;
                case 'date':
                case 'email':
                case 'hidden':
                case 'number':
                case 'search':
                case 'password':
                case 'text':
                case 'currencyAssociatedInputsSum':
                case 'percentage':
                    if (typeof this._config.resultData[this._config.productId[0]][this._outputObject.checked] !== 'undefined' ||
                        typeof this._config.resultData[this._config.productId[0]][this._outputObject.value] !== 'undefined') {
                        resultDataObj[this._outputObject.value] = $elm.val();
                    }
                    else {
                        resultDataObj.components = {};
                        resultDataObj.components.textInputComponents = [];
                        resultDataObj.components.textInputComponents.push({
                            name: $elm[0].name,
                            value: $elm.val(),
                        });
                    }
                    break;
            }
            this._onOutputChange(resultDataObj);
        }
        catch (e) {
            throw 'Wrong data bind.';
        }
    };
    /**
     * @desc callback on select change
     * @param {Event} ev
     * @private
     */
    BSSAbstractComponent.prototype._onSelectChange = function (ev) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _onSelectChange' +
            ' | Params', ev);
        var $elm = this._toolbox.$(ev.currentTarget);
        var resultDataObj = {};
        if (typeof this._outputObject.dropName === 'undefined') {
            resultDataObj[this._outputObject.checked] = $elm.val();
        }
        else {
            var dropPathArr = $elm.data('dropname').split('.');
            var val = $elm.val();
            var rules = $elm.children('[value="' + val + '"]').data('rule');
            resultDataObj.components = {};
            resultDataObj.components.dropdownComponents = [];
            resultDataObj.components.dropdownComponents.push({
                name: $elm.data('dropname'),
                selected: $elm.val(),
                disabled: $elm.prop('disabled'),
            });
            if (typeof rules !== 'undefined') {
                this._applyRules(rules, dropPathArr);
            }
        }
        $elm.trigger('focusout');
        this._onOutputChange(resultDataObj);
    };
    /**
     * @desc applying dropdown rules
     * @param {Object} rules rules to apply
     * @param {Array} dropPathArr array with path to dropdown
     * @private
     */
    BSSAbstractComponent.prototype._applyRules = function (rules, dropPathArr) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _applyRules' +
            ' | Params ', rules, dropPathArr);
        for (var i = 0; i < rules.length; i++) {
            var iteratedRule = rules[i];
            if (iteratedRule.type === 'dropdownComponents') {
                var targetDropdownName = dropPathArr[0] +
                    '.' +
                    dropPathArr[1] +
                    '.' +
                    dropPathArr[2] +
                    '.' +
                    iteratedRule.name;
                var $targetElem = this._entityTemplate.find('[data-dropname="' + targetDropdownName + '"]');
                var targetValues = $targetElem.children('option');
                var isTargetDisabled = iteratedRule.disabled + '' === 'true';
                var targetSelectedVal = iteratedRule.selectedValue;
                var isTargetHidden = iteratedRule.hidden + '' === 'true';
                var ruleInteractContainer = $targetElem.closest('.rule-interact-container');
                var sthChanged = false;
                if (isTargetHidden) {
                    $targetElem.prop('selectedIndex', 0);
                }
                $targetElem.toggleClass('hidden rule-interact-element-hidden', isTargetHidden);
                if (ruleInteractContainer.length) {
                    ruleInteractContainer.toggleClass('hidden rule-interact-container-hidden', isTargetHidden);
                }
                if ($targetElem.prop('disabled') !== isTargetDisabled) {
                    $targetElem.prop('disabled', isTargetDisabled);
                    sthChanged = true;
                }
                if (typeof targetSelectedVal !== 'undefined' &&
                    targetValues.filter('[value="' + targetSelectedVal + '"]').length) {
                    if ($targetElem.val() !== targetSelectedVal) {
                        $targetElem.val(targetSelectedVal);
                        sthChanged = true;
                    }
                }
                if (sthChanged) {
                    $targetElem.trigger('change');
                }
            }
            else if (iteratedRule.type === 'textInputComponents') {
                var targetInputPath = dropPathArr[0] +
                    '.' +
                    dropPathArr[1] +
                    '.textInputComponents.' +
                    iteratedRule.name;
                var $targetElem = this._entityTemplate.find('input[data-value="' + targetInputPath + '.value"]');
                var isTargetDisabled = iteratedRule.disabled + '' === 'true';
                var targetVal = iteratedRule.value;
                var isTargetHidden = iteratedRule.hidden + '' === 'true';
                var ruleInteractContainer = $targetElem.closest('.rule-interact-container');
                var sthChanged = false;
                if (isTargetHidden) {
                    $targetElem.val('');
                }
                $targetElem.toggleClass('hidden rule-interact-element-hidden', isTargetHidden);
                if (ruleInteractContainer.length) {
                    ruleInteractContainer.toggleClass('hidden rule-interact-container-hidden', isTargetHidden);
                }
                if ($targetElem.prop('disabled') !== isTargetDisabled) {
                    $targetElem.prop('disabled', isTargetDisabled);
                    sthChanged = true;
                }
                if (typeof targetVal !== 'undefined' &&
                    $targetElem.val() !== targetVal) {
                    $targetElem.val(targetVal);
                    sthChanged = true;
                }
                if (sthChanged) {
                    $targetElem.trigger('change').trigger('blur');
                }
            }
            else if (iteratedRule.type === 'htmlDomElement') {
                var targetId = iteratedRule.id;
                var $targetElem = this._entityTemplate.find('#' + targetId);
                var isTargetHidden = iteratedRule.hidden + '' === 'true';
                var ruleInteractContainer = $targetElem.closest('.rule-interact-container');
                $targetElem.toggleClass('hidden rule-interact-element-hidden', isTargetHidden);
                if (ruleInteractContainer.length) {
                    ruleInteractContainer.toggleClass('hidden rule-interact-container-hidden', isTargetHidden);
                }
            }
        }
    };
    /**
     * @desc publish event on user interaction with component
     * @emits {bss-component-changed-ENTITYINSTANCEID}
     * @param {object} resultObj - holds refreshed result data
     * @private
     */
    BSSAbstractComponent.prototype._onOutputChange = function (resultObj) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _onOutputChange' +
            ' | Params: ', resultObj);
        var output = {};
        var keys = Object.keys(resultObj);
        if (keys.length && !resultObj.triggerName) {
            resultObj.triggerName = keys[0];
            this._triggerName = keys[0];
        }
        output[this._config.productId[0]] = resultObj;
        if (this._sendAdditionalChangeEvent) {
            this._eventManager.publish('bss-additional-change-event-' + this._config.entityInstanceId, this._config.componentType + '-' + this._config.componentId, output);
        }
        if (this._isOnChangeOff) {
            return;
        }
        this._eventManager.publish('bss-params-changed-' + this._config.entityInstanceId, this._config.componentType + '-' + this._config.componentId, output);
        this._eventManager.publish('bss-component-changed-' + this._config.entityInstanceId, this._config.componentType + '-' + this._config.componentId, output);
    };
    /**
     * @desc updates all the values given in updateMap, call as callback of update event
     * @param {String} publisherName - name of the publisher
     * @param {Object} updateMap - object with new calculated values
     * @emits {bss-component-data-rebound-$COMPONENT-ID}
     * @public
     */
    BSSAbstractComponent.prototype.update = function (publisherName, updateMap) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : update' +
            ' | Params: ', publisherName, updateMap);
        // save update timestamp - for component-critical-error handling
        this._lastUpdateTimestamp = updateMap.updateTimestamp;
        // if there is regular update event (not revert state) and critical error was thrown, do not update this component!
        if (this._stopUpdateFlag && !this._isRevertStateEvent) {
            this._stopUpdateFlag = this._config.stopUpdateFlag;
            return false;
        }
        this._resetErrorObject();
        this._resetValueTags(updateMap);
        this._resetInputs();
        this._resetSelects();
        if (!this._preventAbstractDataBinding) {
            this._validateDataOnChange(updateMap);
        }
        this._triggerTagging();
        this._eventManager.publish('bss-component-data-rebound-' + this._config.componentId, this._config.componentType + '-' + this._config.componentId, updateMap);
        this._stopUpdateFlag = false;
        this._isRevertStateEvent = false;
        return true;
    };
    /**
     * Trigger tagging functionality
     * @protected
     */
    BSSAbstractComponent.prototype._triggerTagging = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _triggerTagging');
        if (this._tagEventOnThrow !== null) {
            this._throwTaggingEvent(this._tagEventOnThrow, true, this._tagTarget);
            this._tagEventOnThrow = null;
        }
    };
    /**
     * @desc updates config to the received data
     * @param {Object} updateMap - object with new calculated values
     * @private
     */
    BSSAbstractComponent.prototype._updateConfig = function (updateMap) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _updateConfig | Params', updateMap);
        //data should be override, there is possibility that some data are removed and are not available anymore in resultData or texts
        if (has(updateMap, 'resultData')) {
            this._toolbox.$.extend(this._config.resultData, updateMap.resultData);
        }
        if (has(updateMap, 'texts')) {
            this._toolbox.$.extend(this._config.texts, updateMap.texts);
        }
    };
    /**
     * @desc clears tag value before update, needed because some data can be removed from resultData/texts and after update they should be empty
     * @param {Object} updateMap - whole object for update data
     * @private
     */
    BSSAbstractComponent.prototype._resetValueTags = function (updateMap) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _resetValueTags | Params', updateMap);
        var valueTags;
        var productIDs = [];
        var selectorTab = [];
        var selectorString;
        if (has(updateMap, 'texts')) {
            //we add product ids from texts
            productIDs = this._toolbox._.keys(updateMap.texts);
            for (var i = 0, len = productIDs.length; i < len; i++) {
                selectorTab.push('value[data-text^="' + productIDs[i] + '"]');
            }
        }
        productIDs = this._toolbox._.keys(updateMap.resultData);
        for (var i = 0, len = productIDs.length; i < len; i++) {
            selectorTab.push('value[data-value^="' + productIDs[i] + '"]');
        }
        selectorString = this._toolbox._.join(selectorTab, ',');
        valueTags = this._toolbox.$(this._componentScope.find('' + selectorString + ''));
        valueTags.text('');
    };
    /**
     * @desc hides all inputs with binding before update, needed because some data can be removed from resultData/texts and after update they shouldn't be available
     * @private
     */
    BSSAbstractComponent.prototype._resetInputs = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _resetInputs');
        if (!this._inputs) {
            return;
        }
        this._formElementsWrapper.removeClass(this._checkedClass);
        this._formElementsWrapper.removeClass(this._disabledClass);
        this._inputs.parent().css('display', 'none');
    };
    /**
     * @desc hides all selects with binding before update, needed because some data can be removed from resultData/texts and after update they shouldn't be available
     * @private
     */
    BSSAbstractComponent.prototype._resetSelects = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _resetSelects');
        if (!this._select) {
            return;
        }
        this._formElementsWrapper.removeClass(this._disabledClass);
        this._select.parent().css('display', 'none');
    };
    Object.defineProperty(BSSAbstractComponent.prototype, "subscriberPrefix", {
        /**
         * @desc getter of _subscriberPrefix (used for recognizing event relation with the components)
         * @return {String}
         */
        get: function () {
            return _subscriberPrefix;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @desc subscribe event
     * @param {Function} callback - subscribe callback
     * @listens {bss-entity-update-$ENTITY-INSTANCE-ID-$PRODUCT-ID}
     * @private
     */
    BSSAbstractComponent.prototype._observeUpdateEvent = function (callback) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _observeUpdateEvent' +
            ' | Params: ', callback);
        if (this.isUpdateSubscriptionOff) {
            return;
        }
        for (var i = 0, length_2 = this._config.productId.length; i < length_2; i++) {
            this._eventManager.subscribe('bss-entity-update-' +
                this._config.entityInstanceId +
                '-' +
                this._config.productId[i], callback, this.subscriberPrefix + this._config.componentId);
        }
    };
    /**
     * Init subComponents
     * @public
     */
    BSSAbstractComponent.prototype.initSubComponents = function () {
        var _this = this;
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _initSubComponents');
        var defaultComponentsData = this.getComponentsData();
        var componentsData = this._config.componentsData
            ? this._toolbox._.merge(defaultComponentsData, this._config.componentsData)
            : defaultComponentsData;
        Object.keys(componentsData).forEach(function (componentName) {
            _this.createSubComponent(componentsData[componentName], componentName);
        });
    };
    /**
     * Get subComponents' default data,
     * components map amd default configs should be set in the end component inherited abstract component
     * @returns {any}
     */
    BSSAbstractComponent.prototype.getComponentsData = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : getComponentsData');
        return {};
    };
    /**
     * Create subcomponent instance
     * @param defaultConfig
     * @param name
     */
    BSSAbstractComponent.prototype.createSubComponent = function (defaultConfig, name) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : createSubComponent' +
            ' | Params: ', defaultConfig, name);
        var customConfig = this._config[name] || {};
        var componentConfig = customConfig
            ? this._toolbox._.merge(defaultConfig, customConfig)
            : defaultConfig;
        componentConfig.resultData = this._config.resultData;
        componentConfig.texts = this._config.texts;
        componentConfig.entityInstanceId = this._config.entityInstanceId;
        componentConfig.testId = "".concat(this._config.testId, "_").concat(name);
        try {
            this.subComponents[name] = new this.subComponentsMap[defaultConfig.componentType](this._entityTemplate, "#".concat(defaultConfig.containerId), componentConfig, this._eventManager, this._formatter, this._resolution, this._toolbox);
        }
        catch (err) {
            this.setErrorObject({
                errorTitle: 'Cannot create some of components',
                errorMsg: 'Something went wrong. Please, try later',
                errorSeverity: 'critical',
            });
        }
    };
    /**
     * @desc unsubscribe all the events of the component, and remove element from DOM
     */
    BSSAbstractComponent.prototype.destroy = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : destroy');
        if (this.isDestroyed) {
            return;
        }
        this._window.off('.' + this._config.componentId);
        this._toolbox
            .$('#' + this._config.containerId + ' *')
            .off('.' + this._config.componentId);
        this._eventManager.unsubscribeByOwner(this.subscriberPrefix + this._config.componentId);
        this._destroyWatchers();
        this._componentTemplate.remove();
        this._destroySubComponents();
        this.isDestroyed = true;
    };
    /**
     * Destroy subcomponents instances
     * @private
     */
    BSSAbstractComponent.prototype._destroySubComponents = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _destroySubComponents');
        var componentsInstances = Object.values(this.subComponents);
        if (componentsInstances && componentsInstances.length) {
            Object.values(this.subComponents).forEach(function (component) {
                component.destroy();
            });
        }
    };
    /**
     * @desc destroys all bound to component watchers
     * @private
     */
    BSSAbstractComponent.prototype._destroyWatchers = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _destroyWatchers');
        for (var i = 0, len = this._watchers.length; i < len; i++) {
            this._watchers[i].destroy();
        }
    };
    /**
     * @desc is use to prepare config data of the component, if necessary implement in the specific component
     * @abstract
     */
    BSSAbstractComponent.prototype._prepareData = function () { };
    /**
     * @desc is use to prepare component scope
     * @abstract
     */
    BSSAbstractComponent.prototype._prepareComponentScope = function () { };
    /**
     * @desc return version of BSSController and EntityBuilder if exist.
     * @return {String} componentVersion
     */
    BSSAbstractComponent.prototype.getVersion = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : getVersion');
        return this._componentVersion;
    };
    /**
     * @desc validation function
     * @return {boolean}
     * @abstract
     */
    BSSAbstractComponent.prototype.validateComponent = function () { };
    /**
     * @desc get container selector
     * @return {String} - component selector string
     */
    BSSAbstractComponent.prototype.getContainerSelector = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : getContainerSelector');
        return this._containerSelector;
    };
    /**
     * @desc get debug mode
     * @return boolean
     */
    BSSAbstractComponent.prototype.getDebugMode = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : getDebugMode');
        return logger.debugMode;
    };
    /**
     * @desc gets value from given <value> tag
     * @param {String|Number} curItem - value to which is used to obtain value
     * @param {Object} [config=null] config - resultData optional object, must be given as flat object without productId
     * @return {String|Number} - value from data based on value tag
     * @private
     */
    BSSAbstractComponent.prototype._getValue = function (curItem, config) {
        if (config === void 0) { config = null; }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _getValue' +
            ' | Param: ', curItem, config);
        if (typeof curItem !== 'string' ||
            !curItem.match(/<value(.*?)><\/value>/)) {
            return curItem;
        }
        var currentObj = this._getValueObject(curItem);
        if (config === null) {
            var currentProductObject = void 0;
            var currentValue = void 0;
            if (!currentObj.productId) {
                currentProductObject = this._config[currentObj.src];
            }
            else {
                currentProductObject =
                    this._config[currentObj.src][currentObj.productId];
            }
            currentValue = currentProductObject[currentObj.value];
            if (currentObj.objSrc && currentProductObject[currentObj.objSrc]) {
                var currentComponentType = currentProductObject[currentObj.objSrc][currentObj.componentType];
                for (var i = 0, len = currentComponentType.length; i < len; i++) {
                    if (currentComponentType[i].name === currentObj.componentUniqueId) {
                        return currentComponentType[i][currentObj.value];
                    }
                }
            }
            else {
                return currentValue;
            }
        }
        else {
            return config[currentObj.value];
        }
    };
    /**
     * @desc method returns result string after binding texts and next resultData
     * @param {String} stringTag
     * @param {Boolean} generalTexts
     * @return {String}
     * @private
     */
    BSSAbstractComponent.prototype._getBoundedString = function (stringTag, generalTexts) {
        if (generalTexts === void 0) { generalTexts = false; }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _getBoundedString' +
            ' | Param: ', stringTag);
        if (!stringTag) {
            return '';
        }
        if (!stringTag.match(/<value(.*?)><\/value>/)) {
            return stringTag;
        }
        var $tag = this._toolbox.$(stringTag);
        var text = $tag.data().text ? $tag.data().text : null;
        var typeText = null;
        var textProduct;
        if (text) {
            typeText = text.split('.')[0];
        }
        textProduct = typeText
            ? typeText
            : generalTexts
                ? 'general'
                : this._config.productId[0];
        this._bindValues(this._config.texts, $tag.filter('[data-text]'), textProduct, 'text');
        if ($tag[0].innerHTML.match(/<value(.*?)><\/value>/)) {
            var $subTag = this._toolbox.$($tag[0].innerHTML);
            var subTagOuterHTML = $subTag[0].outerHTML;
            this._bindValues(this._config.resultData, $subTag, this._config.productId[0], 'value');
            return $tag[0].innerHTML.replace(subTagOuterHTML, $subTag.text());
        }
        this._bindValues(this._config.resultData, $tag.filter('[data-value]'), this._config.productId[0], 'value');
        return $tag[0].innerHTML;
    };
    /**
     * @desc set template
     */
    BSSAbstractComponent.prototype.setTemplate = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : setTemplate' +
            ' | Params: ');
        if (typeof this._config.outsideEntityContainer !== 'undefined' &&
            this._toolbox.$(this._config.outsideEntityContainer).length &&
            this._entityTemplate.parent().data('entity')) {
            this._entityTemplate = this._toolbox.$(this._config.outsideEntityContainer);
            this._containerSelector = this._config.outerContainerId;
        }
        if (this._containerSelector === null) {
            this._entityTemplate.append(this._componentTemplate);
        }
        else {
            this._entityTemplate
                .find(this._containerSelector)
                .append(this._componentTemplate);
        }
    };
    /**
     * @desc set configuration
     * @param {Object} config configuration
     */
    BSSAbstractComponent.prototype.setConfig = function (config) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : setConfig' +
            ' | Params: ', config);
        for (var param in config) {
            if (config.hasOwnProperty(param)) {
                this._config[param] = config[param];
            }
        }
    };
    /**
     * @desc init binding data, given as hole
     * @param {Object} [bindValuesObj=null] bindValuesObj - type of data, 'texts' || 'resultData'
     * @param {String} [dataType=null] dataType - type of data, 'texts' || 'resultData'
     * @private
     */
    BSSAbstractComponent.prototype._bindData = function (bindValuesObj, dataType) {
        if (bindValuesObj === void 0) { bindValuesObj = null; }
        if (dataType === void 0) { dataType = null; }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _bindData' +
            ' | Params: ', bindValuesObj, dataType);
        var valuesSelector;
        var bindData;
        var inc = 0;
        if (dataType === null && typeof bindValuesObj.texts !== 'undefined') {
            valuesSelector = 'text';
            bindData = bindValuesObj.texts;
        }
        else {
            valuesSelector =
                typeof this._outputObject.dropName !== 'undefined'
                    ? 'dropname'
                    : 'value';
            bindData = bindValuesObj.resultData;
        }
        for (var prod in bindData) {
            var values = this._toolbox.$(this._componentScope.find('[data-' + valuesSelector + '^=' + prod + ']'));
            if (inc === 0) {
                var currentProductValues = this._toolbox.$(this._componentScope.find('[data-' + valuesSelector + '^="$productId$"]'));
                if (currentProductValues.length > 0) {
                    values = values.add(currentProductValues);
                }
            }
            if (typeof bindData[prod] === 'object' && 'errors' in bindData[prod]) {
                var prodItem = 'errors';
                for (var errorValue in bindData[prod].errors) {
                    var errors = this._entityTemplate
                        .find('[data-index="' +
                        this._config.resultData[Object.keys(this._config.resultData)[0]]
                            .step +
                        '"]')
                        .find('[data-' +
                        valuesSelector +
                        '="' +
                        prod +
                        '.' +
                        prodItem +
                        '.' +
                        errorValue +
                        '"]');
                    if (inc === 0) {
                        var _currentProductValues = this._entityTemplate
                            .find('[data-index="' +
                            this._config.resultData[Object.keys(this._config.resultData)[0]].step +
                            '"]')
                            .find('[data-' +
                            valuesSelector +
                            '="$productId$' +
                            '.' +
                            prodItem +
                            '.' +
                            errorValue +
                            '"]');
                        if (_currentProductValues.length) {
                            errors = errors.add(_currentProductValues);
                        }
                    }
                    this._bindValues(bindData, errors, prod, valuesSelector);
                }
            }
            inc++;
            if (values.length > 0) {
                this._bindValues(bindData, values, prod, valuesSelector);
            }
        }
        if (valuesSelector === 'text') {
            this._bindData(bindValuesObj, 'value');
        }
        else {
            if (this._inputs.length >= 1) {
                this._bindInputs();
            }
        }
    };
    /**
     * @desc method for determining the state
     * @param {String} data
     * @return {String} state
     * @private
     */
    BSSAbstractComponent.prototype._setState = function (data) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _setState | Param ', data);
        var checkedSrc = data.split('.');
        var product = checkedSrc[0];
        var objSrc = checkedSrc[1];
        var componentType = checkedSrc[2];
        var componentUniqueId = checkedSrc[3];
        var variable = checkedSrc[4];
        var state;
        if (this._toolbox.resultDataFlat &&
            this._toolbox.resultDataFlat.hasOwnProperty(product)) {
            state = this._toolbox.resultDataFlat[product].hasOwnProperty(objSrc)
                ? this._toolbox.resultDataFlat[product][objSrc]
                : this._toolbox.resultDataFlat[product][objSrc +
                    '_' +
                    componentType +
                    '_' +
                    componentUniqueId +
                    '_' +
                    variable];
        }
        return state;
    };
    /**
     * @desc currently function used to update attributes binding on attribute: checked and disabled for inputs
     * @private
     */
    BSSAbstractComponent.prototype._bindInputs = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _bindInputs | Param ');
        var self = this;
        this._toolbox._.each(this._inputs, function (val) {
            var $elm = self._toolbox.$(val);
            var dataChecked = $elm.data('checked');
            var dataDisabled = $elm.data('disabled');
            var dataValue = $elm.data('value');
            var state;
            if (dataDisabled) {
                state = self._setState(dataDisabled);
                if (typeof state !== 'undefined') {
                    if (state === 'true') {
                        $elm.parent().css('display', '');
                        $elm.attr('disabled', 'disabled');
                        self._formElementsWrapper.addClass(self._disabledClass);
                    }
                    else {
                        $elm.parent().css('display', '');
                        $elm.prop('disabled', false);
                    }
                }
            }
            if (dataValue) {
                $elm.parent().css('display', '');
            }
            if (dataChecked) {
                state = self._setState(dataChecked);
                if (val.type === 'checkbox') {
                    if (typeof state !== 'undefined') {
                        if (state === 'true' || state === true) {
                            $elm.parent().css('display', '');
                            $elm.prop('checked', true);
                            self._formElementsWrapper.addClass(self._checkedClass);
                            $elm.trigger('update-click');
                        }
                        else {
                            $elm.parent().css('display', '');
                            $elm.prop('checked', false);
                            $elm.trigger('update-click');
                        }
                    }
                }
                if (val.type === 'radio') {
                    var inputValue = $elm.val();
                    if (inputValue === state) {
                        $elm.parent().css('display', '');
                        $elm.prop('checked', true);
                        self._formElementsWrapper.addClass(self._checkedClass);
                        $elm.trigger('update-click');
                    }
                    else if (typeof state !== 'undefined') {
                        $elm.parent().css('display', '');
                        $elm.prop('checked', false);
                        $elm.trigger('update-deselect');
                    }
                }
            }
        });
    };
    /**
     * @desc method rerender select with new options, new state
     * @param {jQuery|HTMLElement} select
     * @param {String|Object} values string with values or object with full dropdown data (disable status, name, values, rules etc.)
     * @param {Object} bindData
     * @private
     */
    BSSAbstractComponent.prototype._bindSelect = function (select, values, bindData) {
        var _a, _b;
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _bindSelect | Param ', select, values, bindData);
        var valueTab;
        var selectOptionString = '';
        var selectedIndex;
        var isSelectDisabled;
        // externalDictionary for OA dropdown mobile navigation is taken from step navigation triggers, not from response dictionaries
        //  - thus, productId is not reachable for it in this fn, so we secure this condition not to 'return' for externalDictionary
        if (!((_a = this._outputObject) === null || _a === void 0 ? void 0 : _a.productId) && !((_b = this._config) === null || _b === void 0 ? void 0 : _b.externalDictionary)) {
            return;
        }
        if (typeof bindData !== 'undefined') {
            // flat JSON version
            var disabledValue = '';
            var selectedValue = '';
            var optionTab = [];
            var isCheckedValue = void 0;
            var isDisabledValue = void 0;
            var isOptionValue = void 0;
            var componentObject = bindData[this._outputObject.productId];
            var isComponentObject = void 0;
            valueTab = values.split(';');
            if (typeof this._outputObject.objSrc === 'undefined') {
                isComponentObject = componentObject;
            }
            else {
                var currentComponentType = componentObject[this._outputObject.objSrc][this._outputObject.componentType];
                for (var i = 0, len = currentComponentType.length; i < len; i++) {
                    if (currentComponentType[i].name ===
                        this._outputObject.componentUniqueId) {
                        isComponentObject = currentComponentType[i];
                    }
                }
            }
            isCheckedValue = isComponentObject[this._outputObject.checked];
            isDisabledValue = isComponentObject[this._outputObject.disabled];
            isOptionValue = isComponentObject[this._outputObject.option];
            if (this._outputObject.checked && isCheckedValue) {
                if (typeof isCheckedValue !== 'undefined') {
                    selectedValue = isCheckedValue;
                }
                else if (typeof this._outputObject.componentUniqueId === 'undefined') {
                    for (var key in isCheckedValue) {
                        selectedValue = isCheckedValue[key];
                    }
                }
            }
            if (this._outputObject.disabled && isDisabledValue) {
                if (typeof isDisabledValue !== 'undefined') {
                    disabledValue = isDisabledValue;
                }
                else if (typeof this._outputObject.componentUniqueId === 'undefined') {
                    for (var key in isDisabledValue) {
                        disabledValue = isDisabledValue[key];
                    }
                }
            }
            isSelectDisabled = disabledValue === 'true';
            if (this._outputObject.option && isOptionValue) {
                if (typeof isOptionValue !== 'undefined') {
                    optionTab = isOptionValue.split(';');
                }
                else if (typeof this._outputObject.componentUniqueId === 'undefined') {
                    for (var key in isOptionValue) {
                        optionTab = isOptionValue[key].split(';');
                    }
                }
            }
            else {
                //when there are no option text we copy values
                optionTab = valueTab;
            }
            selectedIndex = this._toolbox._.indexOf(valueTab, selectedValue);
            if (selectedIndex === -1) {
                selectedIndex = 0;
            }
            else {
                //we assume that there is default option
                selectedIndex++;
            }
            for (var i = 0, len = valueTab.length; i < len; i++) {
                selectOptionString +=
                    '<option class="bss-option ' +
                        this._optionClass +
                        '" value="' +
                        valueTab[i] +
                        '">';
                var optionText = optionTab[i];
                if (this._outputObject.formatter) {
                    optionText = this._formatter.getFormattedValue(optionTab[i], this._outputObject.formatter);
                }
                selectOptionString += optionText;
                selectOptionString += '</option>';
            }
            select
                .find('option.bss-option')
                .remove()
                .end()
                .append(selectOptionString);
            select[0][selectedIndex].selected = true;
            if (disabledValue === 'true') {
                select.attr('disabled', 'disabled');
                this._formElementsWrapper.addClass(this._disabledClass);
            }
            else {
                select.prop('disabled', false);
                this._formElementsWrapper.removeClass(this._disabledClass);
            }
        }
        else {
            // tree structured JSON version
            var rules = values.rule;
            var rulesAll = void 0;
            var rulesEaches = void 0;
            valueTab = values.values;
            selectedIndex = 0;
            isSelectDisabled = values.disabled + '' === 'true';
            if (typeof rules === 'object') {
                rulesAll = rules.___all;
                rulesEaches = Object.assign({}, rules);
                delete rulesEaches.___all;
            }
            if (typeof valueTab !== 'undefined') {
                for (var i = 0, len = valueTab.length; i < len; i++) {
                    var value = valueTab[i].value;
                    rules = [];
                    if (typeof rulesAll === 'object') {
                        rules.push.apply(rules, rulesAll);
                    }
                    if (typeof rulesEaches === 'object' && value in rulesEaches) {
                        rules.push.apply(rules, rulesEaches[value]);
                    }
                    rules = JSON.stringify(rules);
                    selectOptionString +=
                        '<option class="bss-option ' +
                            this._optionClass +
                            '" value="' +
                            value +
                            '" data-rule=\'' +
                            rules +
                            "'>";
                    var optionText = this._outputObject.optionLiteral
                        ? valueTab[i].key
                        : value;
                    if (this._outputObject.formatter) {
                        var optTextBeforeFormatter = optionText;
                        optionText = this._formatter.getFormattedValue(optTextBeforeFormatter, this._outputObject.formatter);
                    }
                    if (valueTab[i].selected + '' === 'true') {
                        selectedIndex = i + 1;
                    }
                    selectOptionString += optionText;
                    selectOptionString += '</option>';
                }
            }
        }
        select.find('option.bss-option').remove().end().append(selectOptionString);
        select[0][selectedIndex].selected = true;
        if (isSelectDisabled) {
            select.attr('disabled', 'disabled');
            this._formElementsWrapper.addClass(this._disabledClass);
        }
        else {
            select.prop('disabled', false);
            this._formElementsWrapper.removeClass(this._disabledClass);
        }
        //if there is no values in data or valueTab is undefined we didnt show select
        if (valueTab && valueTab.length) {
            select.parent().css('display', '');
        }
    };
    /**
     * @desc appends all the values for given product in proper compoent scope
     * @param  {Object} bindData - description
     * @param  {jQuery|HTMLElement[]} values - tag values
     * @param  {String} prod - product id
     * @param  {String} valuesSelector - part of data attribute selector ("text"/"value"/"dropname")
     * @private
     */
    BSSAbstractComponent.prototype._bindValues = function (bindData, values, prod, valuesSelector) {
        logger.info("AbstractComponent: ".concat(this._config.componentId, " => function : _bindValues | Param "), bindData, values, prod, valuesSelector);
        for (var i = 0, length_3 = values.length; i < length_3; i++) {
            this._bindValue(bindData, values[i], prod, valuesSelector);
        }
    };
    /**
     * @desc appends value for given product in proper compoent scope
     * @param  {Object} bindData - description
     * @param  {jQuery|HTMLElement} proceedValue - tag value
     * @param  {String} prod - product id
     * @param  {String} valuesSelector - part of data attribute selector ("text"/"value"/"dropname")
     * @private
     */
    BSSAbstractComponent.prototype._bindValue = function (bindData, proceedValue, prod, valuesSelector) {
        logger.info("AbstractComponent: ".concat(this._config.componentId, " => function : _bindValue | Param "), bindData, proceedValue, prod, valuesSelector);
        var valueObj = this._toolbox.$(proceedValue); // jQUERY element value
        var format = valueObj.data('formatter');
        var valueObject = valueObj.data(valuesSelector).toString().split('.'); // data-.....  as an Array [product, value name(valueType)]
        var valueType = valueObject[1]; // data-.... value name or 'components'
        var valueComponentType = valueObject[2]; //component type
        var valueComponentId = valueObject[3]; // component id (index)
        var bindValue = bindData[prod][valueType]; // value of value name
        var value = '';
        var _self = this;
        if (typeof bindValue !== 'undefined') {
            if (valuesSelector === 'text' ||
                (valuesSelector === 'value' && typeof bindValue === 'string')) {
                value = bindValue;
            }
            else {
                // bind step validation errors
                if (valueType === 'errors') {
                    value = bindValue[valueComponentType];
                }
                else {
                    if (valueComponentType === 'dropdownComponents' &&
                        valuesSelector === 'dropname') {
                        var currentComponentType = bindValue[valueComponentType];
                        for (var j = 0, len = currentComponentType.length; j < len; j++) {
                            if (currentComponentType[j].name === valueComponentId) {
                                value = currentComponentType[j]; //object with full dropdown data (disable status, name, values, rules etc.)
                            }
                        }
                    }
                    else if (this._toolbox.resultDataFlat &&
                        this._toolbox.resultDataFlat.hasOwnProperty(prod)) {
                        value = this._toolbox.resultDataFlat[prod].hasOwnProperty(valueType)
                            ? this._toolbox.resultDataFlat[prod][valueType]
                            : this._toolbox.resultDataFlat[prod][valueType +
                                '_' +
                                valueComponentType +
                                '_' +
                                valueComponentId +
                                '_' +
                                valuesSelector];
                    }
                }
            }
        }
        if (typeof value !== 'undefined') {
            // put the value inside the element
            if (valueObj.get(0).tagName.toLowerCase() === 'input') {
                // // inputs
                if (valuesSelector === 'text') {
                    if (this._toolbox.$(value).data('value')) {
                        this._setFormElementDataAttr(value, valueObj);
                    }
                    else {
                        valueObj.prop('value', value);
                    }
                }
                else if (value) {
                    valueObj.parent().css('display', '');
                    valueObj.val(value);
                }
            }
            else if (valueObj.get(0).tagName.toLowerCase() === 'select') {
                // // selects
                if (valuesSelector === 'dropname') {
                    this._bindSelect(valueObj, value);
                }
                else {
                    this._bindSelect(valueObj, value, bindData);
                }
            }
            else {
                // // values
                valueObj.empty();
                if (typeof format !== 'undefined') {
                    valueObj.append(this._formatter.getFormattedValue(value, format));
                }
                else {
                    valueObj.append(value);
                }
            }
            // tagging initialization for static links in data-text values
            if (valuesSelector === 'text' &&
                typeof value === 'string' &&
                /<a\s+href=.+<\/a>/.test(value) &&
                valueObj.length) {
                valueObj.find('a').each(function () {
                    _self._initializeTagging(_self._toolbox.$(this));
                });
            }
        }
    };
    /**
     * @desc gets form element configuration from value tag
     * @param {String} valueTag - <value> tag string
     * @return {Object} object with valueName and formatter name
     * @private
     */
    BSSAbstractComponent.prototype._getValueObject = function (valueTag) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _getValueObject', valueTag);
        var valueObj = this._toolbox.$(valueTag);
        var outputObj = {};
        var dataValue = valueObj.data('value');
        var dataText = valueObj.data('text');
        var dataValueObject;
        var dataChecked = valueObj.data('checked');
        var dataDisabled = valueObj.data('disabled');
        var dataOption = valueObj.data('option');
        var dataDropName = valueObj.data('dropname');
        var dataDropSource = valueObj.data('dropsource');
        var dataOptionLiteral = valueObj.data('optionliteral');
        if (dataValue) {
            dataValueObject = dataValue.split('.');
            if (dataValueObject.length === 1) {
                outputObj.formatter = valueObj.data('formatter');
                outputObj.src = 'resultData';
                outputObj.value = dataValueObject[0];
            }
            else {
                outputObj.productId = dataValueObject[0];
                outputObj.formatter = valueObj.data('formatter');
                outputObj.src = 'resultData';
                outputObj.value = dataValueObject[1];
            }
            if (dataValueObject[2]) {
                outputObj.componentUniqueId = dataValueObject[3];
                outputObj.componentType = dataValueObject[2];
                outputObj.objSrc = dataValueObject[1];
                outputObj.value = dataValueObject[4];
            }
        }
        else if (dataDropName) {
            outputObj.productId = dataDropName.split('.')[0];
            outputObj.dropName = dataDropName.split('.')[3];
            outputObj.formatter = valueObj.data('formatter');
            outputObj.src = 'resultData';
            outputObj.optionLiteral = dataOptionLiteral;
        }
        else if (dataDropSource === 'external') {
            outputObj.optionLiteral = dataOptionLiteral;
        }
        else if (dataText) {
            var dataTextObject = dataText.split('.');
            var prodId = dataTextObject[0];
            var prodValue = dataTextObject[1];
            var valueFormText = this._config.texts[prodId][prodValue];
            if (this._toolbox.$(valueFormText).data('value')) {
                dataValue = this._toolbox.$(valueFormText).data('value');
                dataValueObject = dataValue.split('.');
                dataDisabled = valueObj.data('disabled');
                dataChecked = valueObj.data('checked');
                outputObj.value = dataValueObject[1];
                outputObj.productId = dataValueObject[0];
                outputObj.formatter = valueObj.data('formatter');
                outputObj.src = 'resultData';
            }
            else {
                outputObj.value = dataTextObject[1];
                outputObj.productId = dataTextObject[0];
                outputObj.src = 'texts';
                outputObj.formatter = valueObj.data('formatter');
            }
        }
        try {
            var dataDisabledObject = dataDisabled.split('.');
            outputObj.disabled =
                typeof dataDisabledObject[4] !== 'undefined'
                    ? dataDisabledObject[4]
                    : dataDisabledObject[1];
        }
        catch (e) {
            outputObj.disabled = '';
        }
        try {
            var dataCheckedObject = dataChecked.split('.');
            outputObj.checked =
                typeof dataCheckedObject[4] !== 'undefined'
                    ? dataCheckedObject[4]
                    : dataCheckedObject[1];
        }
        catch (e) {
            outputObj.checked = '';
        }
        try {
            var dataOptionObject = dataOption.split('.');
            outputObj.option =
                typeof dataOptionObject[4] !== 'undefined'
                    ? dataOptionObject[4]
                    : dataOptionObject[1];
        }
        catch (e) {
            outputObj.option = '';
        }
        return outputObj;
    };
    /**
     * @desc publish event when critical error occurs
     * @param {Object} errorObj - holds error data
     * @emits {bss-component-critical-error-$ENTITY-INSTANCE-ID'}
     * @private
     */
    BSSAbstractComponent.prototype._onCriticalError = function (errorObj) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _onCriticalError' +
            ' | Params: ', errorObj);
        errorObj.updateTimestamp = this._lastUpdateTimestamp;
        this._eventManager.publish('bss-component-critical-error-' + this._config.entityInstanceId, this._config.componentType + '-' + this._config.componentId, errorObj);
    };
    /**
     * @desc publish event when minor error occurs
     * @param {Object} errorObj - holds error data
     * @emits {bss-component-minor-error-$ENTITY-INSTANCE-ID'}
     * @public
     */
    BSSAbstractComponent.prototype.onMinorError = function (errorObj) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : onMinorError' +
            ' | Params: ', errorObj);
        errorObj.updateTimestamp = this._lastUpdateTimestamp;
        this._eventManager.publish('bss-component-minor-error-' + this._config.entityInstanceId, this._config.componentType + '-' + this._config.componentId, errorObj);
    };
    /**
     * @desc validate component
     * @param {Object} updateMap - new component data (dataResult or Texts)
     * @param {Boolean} [isDataToBind=true] isDataToBind - if set to true update config and rebind data
     * @param {Boolean} [rebindStaticTexts=false] rebindStaticTexts - if set to true rebind static texts from this._toolbox.layoutTexts
     * @protected
     */
    BSSAbstractComponent.prototype._validateDataOnChange = function (updateMap, isDataToBind, rebindStaticTexts) {
        if (isDataToBind === void 0) { isDataToBind = true; }
        if (rebindStaticTexts === void 0) { rebindStaticTexts = false; }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _validateDataOnChange' +
            ' | Params: ', updateMap, isDataToBind);
        switch (this._errorType) {
            case null:
                if (isDataToBind) {
                    this._updateConfig(updateMap);
                }
                if (rebindStaticTexts &&
                    typeof this._toolbox.layoutTexts === 'object') {
                    this._bindData(Object.assign({ texts: this._toolbox.layoutTexts }, updateMap));
                }
                else {
                    this._bindData(updateMap);
                }
                break;
            case 'minor':
                this.onMinorError(this._errorObj);
                break;
            case 'critical':
                this._onCriticalError(this._errorObj);
                break;
        }
    };
    /**
     * @desc set component error object attributes
     * @param {String} errorTitle - component error message text
     * @param {String} errorMsg - component error message text
     * @param {String} errorSeverity - component error importance: 'critical', 'minor'
     * @public
     */
    BSSAbstractComponent.prototype.setErrorObject = function (_a) {
        var errorTitle = _a.errorTitle, errorMsg = _a.errorMsg, errorSeverity = _a.errorSeverity;
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : setErrorObject' +
            ' | Params: ', errorTitle, errorMsg, errorSeverity);
        this._errorType = errorSeverity;
        this._errorObj = {
            entityInstanceId: this._config.entityInstanceId,
            componentId: this._config.componentId,
            componentSignificance: this._config.significance,
            errorSeverity: errorSeverity,
            errorMsg: errorMsg,
            errorTitle: errorTitle,
            offerId: this._config.productId[0],
        };
    };
    /**
     * @desc reset component error object
     * @private
     */
    BSSAbstractComponent.prototype._resetErrorObject = function () {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _resetErrorObject');
        this._errorType = null;
        this._errorObj = {};
    };
    Object.defineProperty(BSSAbstractComponent.prototype, "errorObject", {
        /**
         * @desc getter for error object
         * @return {Object|{}|*}
         */
        get: function () {
            return this._errorObj;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @desc default destroy of component
     * @param {Array} [prodIds=null] array of invalid products
     */
    BSSAbstractComponent.prototype.switchOffProduct = function (prodIds) {
        if (prodIds === void 0) { prodIds = null; }
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : switchOffProduct' +
            ' | Params: ', prodIds);
        this.destroy();
    };
    /**
     * @desc checks that list is sorted, throw error when list is not sorted
     * @param {Array} list - list to check
     * @return {Boolean} - true if list is correct, false if not
     * @private
     */
    BSSAbstractComponent.prototype._isSliderListCorrect = function (list) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _isSliderListCorrect' +
            ' | Params: ', list);
        for (var i = 0, length_4 = list.length; i < length_4 - 1; i++) {
            if (!(list[i] < list[i + 1])) {
                if (list[i] === list[i + 1]) {
                    this.setErrorObject({
                        errorMsg: 'config -> variableList values are duplicated',
                        errorSeverity: 'critical',
                    });
                    return false;
                }
                this.setErrorObject({
                    errorMsg: 'config -> variableList or linked value is not sorted',
                    errorSeverity: 'critical',
                });
                return false;
            }
        }
        var uniqueList = this._toolbox._.uniq(list);
        if (uniqueList.length !== list.length) {
            this.setErrorObject({
                errorMsg: 'config -> variableList values are duplicated',
                errorSeverity: 'critical',
            });
            return false;
        }
        return true;
    };
    /**
     * @desc check whether the limit has changed
     * @param {Object} updateObj - data received from BSSConnector after request
     * @param {Object} sliderLimitVariables - object with slider limits variables
     * @return {Boolean} - true if limit has changed, false if not
     * @private
     */
    BSSAbstractComponent.prototype._isSliderLimitChanged = function (updateObj, sliderLimitVariables) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _isSliderLimitChanged' +
            ' | Params: ', updateObj, sliderLimitVariables);
        for (var item in sliderLimitVariables.numericalObj) {
            if (sliderLimitVariables.numericalObj[item] !==
                this._getValue(sliderLimitVariables[item], updateObj)) {
                sliderLimitVariables.numericalObj[item] = this._getValue(sliderLimitVariables[item], updateObj);
                return true;
            }
        }
        return false;
    };
    /**
     * @desc prepares limit obj
     * @param {Object} sliderLimitVariables - object with slider limits variables
     * @private
     */
    BSSAbstractComponent.prototype._prepareLimitObj = function (sliderLimitVariables) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _prepareLimitObj' +
            ' | Params: ', sliderLimitVariables);
        for (var item in sliderLimitVariables) {
            if (item !== 'numericalObj' &&
                typeof this._config[item] !== 'undefined') {
                sliderLimitVariables[item] = this._config[item];
                sliderLimitVariables.numericalObj[item] = this._getValue(sliderLimitVariables[item]);
            }
        }
    };
    /**
     * @desc calculate and return closest possible number
     * @param {Number} value - value for which method calculate closest number
     * @param {Array} sliderValues - list of value for the slider
     * @return {Number} - closest number from slider configuration
     * @private
     */
    BSSAbstractComponent.prototype._getClosestNumber = function (value, sliderValues) {
        logger.info('AbstractComponent: ' +
            this._config.componentId +
            ' => function : _getClosestNumber' +
            ' | Params: ', value, sliderValues);
        value = +value;
        var indexOfValue = sliderValues.indexOf(value);
        var sliderValue = sliderValues[sliderValues.length - 1];
        if (sliderValues.indexOf(value) !== -1) {
            sliderValue = sliderValues[indexOfValue];
        }
        else {
            for (var i = 0, length_5 = sliderValues.length; i < length_5; i++) {
                if (value < sliderValues[i]) {
                    if (Math.abs(value - sliderValues[i]) <
                        Math.abs(value - sliderValues[i - 1]) ||
                        i === 0) {
                        sliderValue = sliderValues[i];
                        break;
                    }
                    else {
                        sliderValue = sliderValues[i - 1];
                        break;
                    }
                }
            }
        }
        /**
         * needed because javascript incorrectly calculated action for floating point numbers
         * 0.14 / 100 = 0.0014000000000000002 (bad)
         */
        if (typeof this._config.formatter !== 'undefined') {
            var formattedVal = this._formatter.getFormattedValue(sliderValue, this._config.formatter);
            return this._formatter.getValue(formattedVal, this._config.formatter);
        }
        else {
            return sliderValue;
        }
    };
    return BSSAbstractComponent;
}());
export default BSSAbstractComponent;
